import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { isValid as dateIsValid } from 'date-fns';
import isNil from 'lodash/isNil';

export interface SelectOption {
  value: string
  label?: string
}

export interface SelectOptionGroup {
  label: string
  options: SelectOption[]
}

const isEmpty = (value): boolean => value === '' || isNil(value);
const isNumber = (value): boolean => !Number.isNaN(Number(value)); // parseable number
export const isInteger = (value): boolean => Number.isInteger(Number(value)) && !String(value).includes('.');

export const arrayLengthMin = (size: number): ValidatorFn => control => {
  if (control.value.length < size) {
    return { arrayLength: `At least ${size} value${size === 1 ? ' is' : 's are'} required.` };
  }
  return null;
};

export const validJson: ValidatorFn = control => {
  if (!control.value) {
    return null;
  }
  try {
    JSON.parse(control.value);
  } catch (error) {
    return { validJson: 'JSON could not be parsed.' };
  }
  return null;
};

// Should be covered by input[type="number"]
export const typeNumber: ValidatorFn = control => {
  if (isEmpty(control.value)) {
    return null;
  }
  return isNumber(control.value) ? null : { typeNumber: 'Value is not a number.' };
};

export const numberInteger: ValidatorFn = control => {
  if (isEmpty(control.value) || !isNumber(control.value)) {
    return null; // defer typeNumber check
  }
  return isInteger(control.value) ? null : { numberInteger: 'Number is not an integer.' };
};

export const validDate: ValidatorFn = control => {
  if (!control.value) {
    return null;
  }
  return dateIsValid(new Date(control.value)) ? null : { validDate: 'Value is not a date.' };
};

export const validFileName = Validators.pattern(/^[^:*/"?>|<]+$/);

export const validMimeType = Validators.pattern(/^\w+\/[-+\w.]+$/);

export const prohibitValues = (values: string[]): ValidatorFn => control => {
  if (control.value && values.some(val => val === control.value)) {
    return { prohibitValues: 'Value is prohibited.' };
  }
  return null;
};

/**
 * Validates that the control is a valid folder name using the following conditions
 * If a segment has an opening and closing brace {}, it has the same number of opening and closing braces,
 * Each segment is separated by a slash
 * There are no leading or trailing slashes
 * The input contains at least one segment
 *
 * @param control
 * @returns null or an error
 */
export const validFolderName: ValidatorFn = (control: AbstractControl) => {
  // Only split on '/' not within '{}'
  const segments = (control.value ?? '').split(/(?<=^[^{}]*)\/(?=.*{)|(?<=}.*)\/(?=.*{)|(?<=}.*)\/(?=[^{}]*$)/);
  const validSegments = segments.reduce((previousValue: boolean, currentSegment: string) => {
    if (!previousValue) {
      return previousValue;
    } else {
      const openingCount = currentSegment.match(/{/g)?.length || 0;
      const closingCount = currentSegment.match(/}/g)?.length || 0;
      const start = currentSegment.match(/^\//)?.length || 0;
      const end = currentSegment.match(/\/$/)?.length || 0;

      return currentSegment.length && !start && !end && openingCount === closingCount;
    }
  }, true);

  if (!control.value || validSegments) {
    return null;
  } else {
    return { invalidFolderName: control.value };
  }
};

/* ensures the phone number 7-15 digits, ignoring all non-numeric input */
export const validPhoneNumber: ValidatorFn = control => {
  if (!control.value) {
    return null;
  }

  const phoneNumberPattern = new RegExp(/^\d{1,15}$/g);
  const nonDigitPattern = new RegExp(/\D/g);
  const scrubbed = control.value.replace(nonDigitPattern, '');
  const validPhoneNumber = phoneNumberPattern.test(scrubbed);
  return validPhoneNumber ? null : { invalidPhoneNumber: 'Value is not a valid phone number.' };
};

export const validRegex: ValidatorFn = control => {
  const error = { invalidRegex: 'invalid regex' };
  if (!control.value) {
    return error;
  }
  try {
    new RegExp(control.value);
  } catch (e) {
    return error;
  }
  return null;
};

/**
 * Validates items in an FormControl holding an array using an item ValidatorFn.
 * Errors are combined into an `arrayItems` property that reports the first element
 * value that does not pass the validator.
 */
export const arrayItemValidator = (itemValidator: ValidatorFn): ValidatorFn => (arrayControl: AbstractControl) => {
  let arrayErrors: ValidationErrors = {};
  for (const [position, item] of Object.entries(arrayControl.value)) {
    const control = new FormControl(item);
    const errors = itemValidator(control);
    for (const key of Object.keys(errors || {})) {
      const message = errors[key];
      errors[key] = [Number(position), item, message];
    }
    arrayErrors = { ...errors, ...arrayErrors }; // keep the errors from earlier items
  }
  return Object.keys(arrayErrors).length > 0 ? { arrayItems: arrayErrors } : null;
};

export function resetFormGroup(formGroup: FormGroup, controls: Record<string, AbstractControl>): void {
  for (const key of Object.keys(formGroup.controls)) {
    formGroup.removeControl(key);
  }
  for (const [key, control] of Object.entries(controls)) {
    formGroup.registerControl(key, control);
  }
  formGroup.updateValueAndValidity();
  formGroup.markAsPristine();
}
